import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, take } from 'rxjs';
import { OrderDetails } from '../types/order.types';
import { PunchBackMessage } from '../types/punchout.types';
import { MarketService } from './market.service';
import { StorageService } from './storage.service';
import { UserService } from './user/user.service';

const PUNCHOUT_STORAGE: string = 'PUNCHOUT';

@Injectable({
	providedIn: 'root',
})
export class PunchoutService {
	private _isPunchoutSubject: ReplaySubject<boolean> = new ReplaySubject(1);
	public isPunchout$ = this._isPunchoutSubject.asObservable();

	constructor(
		private storageService: StorageService,
		private userService: UserService,
		private marketService: MarketService,
		private router: Router,
		private httpClient: HttpClient,
	) {}

	public getPunchoutSessionId(): string | null {
		return location.href.split('punchout/')[1] || this.storageService.getItem(PUNCHOUT_STORAGE);
	}

	public initializePunchout(): Observable<boolean> {
		return new Observable<boolean>((observer) => {
			const punchoutSessionId = this.getPunchoutSessionId();
			const isPunchout = Boolean(punchoutSessionId);

			this._isPunchoutSubject.next(isPunchout);

			if (!isPunchout) {
				observer.next(isPunchout);
				observer.complete();
				return;
			}

			this.storageService.setItem(PUNCHOUT_STORAGE, punchoutSessionId);

			this.userService
				.getUser()
				.pipe(take(1))
				.subscribe(() => {
					this.router.navigate([this.marketService.currentLanguage, 'punchout-start']);
					observer.next(isPunchout);
					observer.complete();
				});
		}).pipe(take(1));
	}

	public sendPuncoutOrder(language: string, orderDetails: OrderDetails): Observable<PunchBackMessage> {
		const sessionId = this.storageService.getItem(PUNCHOUT_STORAGE);
		return this.httpClient.post<PunchBackMessage>(
			`/webapi/punchout/v2/${sessionId}/complete?language=${language}`,
			orderDetails,
		);
	}

	public cancelPunchout(): Observable<PunchBackMessage> {
		const sessionId = this.storageService.getItem(PUNCHOUT_STORAGE);
		this.logoutPunchout();
		return this.httpClient.post<PunchBackMessage>(`/webapi/punchout/${sessionId}/cancel`, {});
	}

	public logoutPunchout(): void {
		this.storageService.removeItem(PUNCHOUT_STORAGE);
	}

	public submitPunchbackForm(msg: PunchBackMessage): HTMLFormElement {
		const form = document.createElement('form');
		form.method = 'POST';
		form.enctype = 'application/x-www-form-urlencoded';
		form.action = msg.url;

		msg.attributes.forEach((attr) => {
			form.setAttribute(attr.name, attr.value);
		});

		msg.values.forEach((value) => {
			const input = document.createElement('input');
			input.type = 'hidden';
			input.name = value.name;
			input.value = value.value;
			form.appendChild(input);
		});

		document.body.appendChild(form);
		form.submit();

		return form;
	}
}
